// Vector port of PrintableToc.js (the "Interactive PDF" Contents page).
// Unlike the old screenshot pipeline, which read row pixel-rects back off
// the DOM via getBoundingClientRect to lay pdf.link() overlays on top of a
// captured image, drawToc() here returns each item's rect in the same mm
// coordinate space it just drew in - the caller can pass those straight to
// pdf.link() with no DOM/pixel translation step at all.
import { t } from "i18next";
import { PRINT_THEME as T } from "./printTheme";
import { getTournamentExportHeaderInfo } from "./exportUtils";
import { px2pt, px2mm, lineHeightMm, drawText, hLine, drawCircularImage } from "./pdfKit";
import { measureFooter } from "./pdfFooter";

const TITLE_FS = px2pt(26);
const META_FS = px2pt(13);
const CONTENTS_FS = px2pt(15);
const CATEGORY_FS = px2pt(13);
const ITEM_FS = px2pt(14);

const LOGO_MM = px2mm(56);
const HEADER_PAD_BOTTOM = px2mm(18);
const HEADER_MARGIN_BOTTOM = px2mm(24);
const CONTENTS_TITLE_H = lineHeightMm(CONTENTS_FS, 1.2) + px2mm(14);
const CATEGORY_H = lineHeightMm(CATEGORY_FS, 1.3) + px2mm(16);
const CATEGORY_MARGIN_BOTTOM = px2mm(4);
const ITEM_H = lineHeightMm(ITEM_FS, 1.2) + px2mm(20);
const CATEGORY_GROUP_MARGIN_BOTTOM = px2mm(10);

export const measureTocHeader = () => {
    const titleH = lineHeightMm(TITLE_FS, 1.1);
    const metaH = lineHeightMm(META_FS, 1.2) + px2mm(4);
    const rowH = Math.max(LOGO_MM, titleH + metaH);
    return rowH + HEADER_PAD_BOTTOM + HEADER_MARGIN_BOTTOM;
};

export const measureToc = (categories) => {
    let h = measureTocHeader() + CONTENTS_TITLE_H;
    categories.forEach(cat => {
        h += CATEGORY_H + CATEGORY_MARGIN_BOTTOM + cat.items.length * ITEM_H + CATEGORY_GROUP_MARGIN_BOTTOM;
    });
    return h + measureFooter();
};

export const drawTocHeader = (pdf, x, y, tournament, maxWidthMm, logoDataUrl) => {
    const info = getTournamentExportHeaderInfo(tournament, "");
    const metaLine = [info.club, info.location, info.dateRange].filter(Boolean).join("  ·  ");
    const textX = logoDataUrl ? x + LOGO_MM + px2mm(14) : x;
    const textMaxW = maxWidthMm - (logoDataUrl ? LOGO_MM + px2mm(14) : 0);

    if (logoDataUrl) drawCircularImage(pdf, logoDataUrl, x + LOGO_MM / 2, y + LOGO_MM / 2, LOGO_MM);

    const titleH = drawText(pdf, info.name || t("Tournament"), textX, y, {
        fontSizePt: TITLE_FS, color: T.ink, maxWidthMm: textMaxW, multiplier: 1.1,
    });
    if (metaLine) {
        drawText(pdf, metaLine, textX, y + titleH + px2mm(4), { fontSizePt: META_FS, color: T.muted, maxWidthMm: textMaxW });
    }

    const rowH = Math.max(LOGO_MM, titleH + lineHeightMm(META_FS, 1.2) + px2mm(4));
    const borderY = y + rowH + HEADER_PAD_BOTTOM;
    hLine(pdf, x, borderY, maxWidthMm, T.border, px2mm(2));
    return borderY + HEADER_MARGIN_BOTTOM;
};

export const drawToc = (pdf, x, y, tournament, categories, maxWidthMm, logoDataUrl) => {
    let cy = drawTocHeader(pdf, x, y, tournament, maxWidthMm, logoDataUrl);

    drawText(pdf, t("Contents").toUpperCase(), x, cy, { fontSizePt: CONTENTS_FS, color: T.muted });
    cy += CONTENTS_TITLE_H;

    const itemRects = {};

    categories.forEach(cat => {
        pdf.setFillColor(T.superWhite);
        pdf.setDrawColor(T.border);
        pdf.setLineWidth(0.15);
        pdf.roundedRect(x, cy, maxWidthMm, CATEGORY_H, px2mm(5), px2mm(5), "FD");
        drawText(pdf, cat.label, x + px2mm(14), cy + CATEGORY_H / 2 - lineHeightMm(CATEGORY_FS, 1) / 2, {
            fontSizePt: CATEGORY_FS, color: T.ink,
        });
        cy += CATEGORY_H + CATEGORY_MARGIN_BOTTOM;

        cat.items.forEach(item => {
            drawText(pdf, item.label, x + px2mm(26), cy + ITEM_H / 2 - lineHeightMm(ITEM_FS, 1) / 2, {
                fontSizePt: ITEM_FS, color: T.ink, maxWidthMm: maxWidthMm - px2mm(60),
            });
            pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
            pdf.setFontSize(px2pt(16));
            pdf.setTextColor(T.muted);
            pdf.text("›", x + maxWidthMm - px2mm(14), cy + ITEM_H / 2 + px2mm(1.5), { align: "right" });
            hLine(pdf, x, cy + ITEM_H, maxWidthMm, T.border, px2mm(0.5));

            itemRects[item.key] = { x, y: cy, w: maxWidthMm, h: ITEM_H };
            cy += ITEM_H;
        });

        cy += CATEGORY_GROUP_MARGIN_BOTTOM;
    });

    return { nextY: cy, itemRects };
};
